import React, { useContext, useEffect, useRef, useState } from 'react';
import { Switch, Route, Redirect, useHistory, useLocation } from 'react-router-dom';
import '../crm.css';

// Import auth context and utilities
import { AuthUserContext } from '../../../components/Session';
import { isFranchiseeEmail } from '../../../utils/dashboardRouting';

// Import CRM components
import CRMLayout from '../CRMLayout';
import CRMDashboard from './CRMDashboard';
import LeadsPage from './LeadsPage';
import LeadDetailPage from './LeadDetailPage';
import ContactsPage from './ContactsPage';
import ContactDetailPage from './ContactDetailPage';
import DealsPage from './DealsPage';
import DealDetailPage from './DealDetailPage';
import ActivitiesPage from './ActivitiesPage';
import PropertiesPage from './PropertiesPage';
import DuplicatesPage from './DuplicatesPage';
import AuditPage from './AuditPage';
import TeamPage from './TeamPage';
import AutomationsPage from './AutomationsPage';
import LeadSourcesPage from './LeadSourcesPage';
import { getCrmNetworkActivitySnapshot, subscribeCrmNetworkActivity } from '../utils/crmApi';

// Main CRM App
const CRMApp = ({ firebase }) => {
    const authUser = useContext(AuthUserContext);
    const location = useLocation();
    const history = useHistory();
    const [accessDenied, setAccessDenied] = useState(false);
    const [accessChecked, setAccessChecked] = useState(false);
    const [crmNetwork, setCrmNetwork] = useState(() => getCrmNetworkActivitySnapshot());
    const lastClickedButtonRef = useRef(null);

    // Check access on mount
    useEffect(() => {
        // Check authentication and franchisee status
        if (!authUser) {
            setAccessDenied(true);
            setAccessChecked(true);
            return;
        }

        if (!isFranchiseeEmail(authUser.email)) {
            setAccessDenied(true);
            setAccessChecked(true);
            return;
        }

        setAccessDenied(false);
        setAccessChecked(true);
    }, [authUser]);

    useEffect(() => {
        const unsubscribe = subscribeCrmNetworkActivity((snapshot) => {
            setCrmNetwork(snapshot);
        });
        return unsubscribe;
    }, []);

    useEffect(() => {
        const onClickCapture = (event) => {
            const target = event.target;
            if (!target || typeof target.closest !== 'function') return;
            const shell = target.closest('[data-crm-app-shell]');
            if (!shell) return;
            const button = target.closest('.crm-btn');
            if (!button) return;
            if (lastClickedButtonRef.current && lastClickedButtonRef.current !== button) {
                lastClickedButtonRef.current.classList.remove('crm-btn-loading');
            }
            lastClickedButtonRef.current = button;
            if (crmNetwork?.busy) {
                button.classList.add('crm-btn-loading');
            }
        };

        document.addEventListener('click', onClickCapture, true);
        return () => {
            document.removeEventListener('click', onClickCapture, true);
        };
    }, [crmNetwork?.busy]);

    useEffect(() => {
        document.body.classList.toggle('crm-network-busy', !!crmNetwork?.busy);
        if (crmNetwork?.busy) {
            if (lastClickedButtonRef.current?.isConnected) {
                lastClickedButtonRef.current.classList.add('crm-btn-loading');
            }
        } else if (lastClickedButtonRef.current) {
            lastClickedButtonRef.current.classList.remove('crm-btn-loading');
        }

        return () => {
            document.body.classList.remove('crm-network-busy');
            if (lastClickedButtonRef.current) {
                lastClickedButtonRef.current.classList.remove('crm-btn-loading');
            }
        };
    }, [crmNetwork]);

    // Show loading while checking access
    if (!accessChecked) {
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                background: '#f9fafb',
            }}>
                <div className="crm-spinner"></div>
            </div>
        );
    }

    // Access denied
    if (accessDenied) {
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                background: '#f9fafb',
            }}>
                <div style={{
                    background: 'white',
                    padding: '48px',
                    borderRadius: '8px',
                    boxShadow: '0 4px 24px rgba(0,0,0,0.1)',
                    textAlign: 'center',
                    maxWidth: '400px',
                }}>
                    <h2 style={{ margin: '0 0 16px', color: '#1f2937' }}>Access Denied</h2>
                    <p style={{ margin: '0 0 24px', color: '#6b7280' }}>
                        CRM can only be accessed through the Franchise Dashboard.
                    </p>
                    <button
                        onClick={() => {
                            history.push('/franchise-dashboard');
                        }}
                        style={{
                            background: '#1d4ed8',
                            color: 'white',
                            border: 'none',
                            padding: '12px 24px',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: 'pointer',
                        }}
                    >
                        Go to Franchise Dashboard
                    </button>
                </div>
            </div>
        );
    }

    const basePath = location.pathname.startsWith('/franchise-dashboard/leads/crm')
        ? '/franchise-dashboard/leads/crm'
        : '/CRM/franchise-crm';

    const networkLabel = crmNetwork?.busy
        ? (
            crmNetwork?.lastRequest?.label
            || ((crmNetwork?.lastRequest?.method || 'GET') === 'GET' ? 'Loading CRM data...' : 'Saving changes...')
        )
        : '';

    return (
        <CRMLayout>
            <div className={`crm-network-indicator ${crmNetwork?.busy ? 'active' : ''}`} aria-live="polite" aria-atomic="true">
                <div className="crm-network-progress-bar" />
                <div className="crm-network-pill">
                    <span className="crm-spinner crm-spinner-inline" />
                    <span>{networkLabel}</span>
                    {crmNetwork?.activeCount > 1 && <span className="crm-network-pill-count">({crmNetwork.activeCount})</span>}
                </div>
            </div>
            <Switch>
                <Route exact path={basePath} component={CRMDashboard} />
                <Route exact path={`${basePath}/dashboard`} component={CRMDashboard} />
                <Route path={`${basePath}/leads/:id`} component={LeadDetailPage} />
                <Route path={`${basePath}/leads`} component={LeadsPage} />
                <Route path={`${basePath}/deals/:id`} component={DealDetailPage} />
                <Route path={`${basePath}/deals`} component={DealsPage} />
                <Route path={`${basePath}/properties/:id`} component={PropertiesPage} />
                <Route path={`${basePath}/properties`} component={PropertiesPage} />
                <Route path={`${basePath}/duplicates`} component={DuplicatesPage} />
                <Route path={`${basePath}/contacts/:id`} component={ContactDetailPage} />
                <Route path={`${basePath}/contacts`} component={ContactsPage} />
                <Route path={`${basePath}/activities`} component={ActivitiesPage} />
                <Route exact path={`${basePath}/lead-sources`} component={LeadSourcesPage} />
                <Route exact path={`${basePath}/audit`} component={AuditPage} />
                <Route exact path={`${basePath}/automations`} component={AutomationsPage} />
                <Route exact path={`${basePath}/team`} component={TeamPage} />

                {/* Manual Section */}
                {/* Note: The original instruction for this line was 'Route to={`${basePath}/manual`} />'.
                   This is not valid syntax for a React Router 'Route' component.
                   Assuming the intent was to redirect, it has been changed to a 'Redirect' component.
                   If a specific component should be rendered for '/manual', please update this line. */}
                <Redirect from={`${basePath}/manual`} to={`${basePath}`} /> {/* Corrected to a valid Redirect */}
                <Redirect to={basePath} />
            </Switch>
        </CRMLayout>
    );
};

export default CRMApp;
